import {isFieldRequired} from '@wc-stripe/utils';
import BaseGateway from './BaseGateway';

/**
 * Abstract Express Gateway
 *
 * Base class for express checkout payment methods (Link, Apple Pay, Google Pay)
 * Provides common functionality for Stripe's Express Checkout Element
 *
 * @since 4.0.0
 */
class AbstractExpressGateway extends BaseGateway {

    type;

    _isEligible = null;

    _isDisabled = false;

    elementType = 'expressCheckout';

    _isOpen = false;

    _styles = {
        clear: 'both',
        minHeight: '40px'
    }

    constructor(props) {
        super(props);
        this.capabilities = [...this.capabilities, 'elements', 'payment_sheet'];
    }

    /**
     * Create and mount the express checkout element
     * @param {HTMLElement} container - Container to mount the element
     */
    async mountPaymentElement(container) {
        // make sure we clear all existing handlers.
        this.paymentElement.removeAllListeners();
        // Bind event handlers
        this.paymentElement.on('ready', this.onReady.bind(this));
        this.paymentElement.on('loaderror', this.onLoadError.bind(this));
        this.paymentElement.on('click', this.onClickElement.bind(this));
        this.paymentElement.on('confirm', this.onConfirm.bind(this));
        this.paymentElement.on('cancel', this.onCancel.bind(this));
        this.paymentElement.on('shippingaddresschange', this.onShippingDataChange.bind(this, 'shippingaddresschange'));
        this.paymentElement.on('shippingratechange', this.onShippingDataChange.bind(this, 'shippingratechange'));

        // Mount the element
        this.paymentElement.mount(container);
    }

    /**
     * Check if the payment method can make payments
     * @returns {Promise<boolean>}
     */
    async canMakePayment() {
        try {
            // Cart, product, and minicart pages allow zero totals (product may not be in cart yet).
            // Checkout express checkout and order pay require a non-zero total unless the mode is "subscription".
            const allowZeroTotal = this.context.isProduct() || this.isSetupMode() || this.isSubscriptionMode();
            if (!allowZeroTotal && this.controller.getTotalCents() === 0) {
                return false;
            }
            return this.isEligible();
        } catch (error) {
            console.log('Payment method is not available. ', error);
            return false;
        }
    }

    /**
     * Returns promise that resolves with available payment methods
     * @returns {Promise<Object>}
     */
    isEligible() {
        if (this._isEligible === null) {
            this._isEligible = new Promise((resolve, reject) => {
                const div = document.createElement('div');
                div.style.display = 'none';
                document.querySelector('body').appendChild(div);
                this.paymentElement.on('ready', ({availablePaymentMethods = {}}) => {
                    resolve(!!availablePaymentMethods[this.type]);
                    div.remove();
                    this.paymentElement.removeAllListeners();
                });
                this.paymentElement.on('loaderror', (error) => {
                    reject(error);
                    div.remove();
                });
                this.paymentElement.mount(div);
            });
        }
        return this._isEligible;
    }

    /**
     * Handle ready event from Stripe
     * @param {Object} event - Event object containing availablePaymentMethods
     */
    onReady({availablePaymentMethods = {}}) {
        this.actions.doAction('express_element_ready', availablePaymentMethods, this);
    }

    /**
     * Handle load error event
     * @param {Object} event
     */
    onLoadError(event) {
        console.error('Express checkout element load error:', event);
        this.controller.submitError(event.error);
    }

    /**
     * Handle click event before payment sheet opens
     * Pass initial data to the payment sheet
     * @param {Object} event
     */
    async onClickElement(event) {
        if (this._isDisabled) {
            return event.reject();
        }

        try {
            // Notify controller
            this.controller.onClick();
        } catch (error) {
            return event.reject();
        }

        this._isOpen = true;

        // Prepare initial data for payment sheet
        const data = {};
        const totalCents = this.controller.getTotalCents();

        // Add line items if available
        const lineItems = this.controller.getLineItems();
        if (lineItems && lineItems.length > 0) {
            const calculatedTotal = lineItems.reduce((total, item) => total + item.amountCents, 0);

            if (calculatedTotal === totalCents) {
                data.lineItems = this.transformLineItems(lineItems);
            }
        }

        // Add shipping rates if needed
        if (this.controller.needsShipping()) {
            const shippingOptions = this.controller.getShippingOptions();
            if (shippingOptions && shippingOptions.length > 0) {
                data.shippingRates = this.transformShippingRates(
                    shippingOptions,
                    this.controller.getSelectedShippingMethod()
                );
            }
        }

        // Ensure the elements have the current total in cents
        // Setup mode doesn't support the "amount" property.
        if (!this.isSetupMode() && totalCents > 0) {
            this.elements.update({
                amount: this.controller.getTotalCents()
            });
        }

        // Allow customization via filter
        const response = await this.actions.applyFilters('express_click_data', data, this, event);

        // Resolve with initial data
        event.resolve(response);
    }

    /**
     * Handle confirm event when user completes payment in sheet
     * @param {Object} event
     */
    async onConfirm(event) {
        try {
            // Submit the Stripe Elements to prepare for payment method creation
            await this.elements.submit();

            // Create the payment method
            const response = await this.stripe.createPaymentMethod({
                elements: this.elements
            });

            if (response.error) {
                this.controller.submitError(response.error);
                return;
            }

            this.setPaymentMethodId(response.paymentMethod.id);

            // Extract standardized checkout data and submit
            const checkoutData = this.extractStandardizedCheckoutData(event, response.paymentMethod);

            this.controller.submit(checkoutData);
        } catch (error) {
            event.paymentFailed({
                reason: 'fail',
                message: error.message
            });
        }
    }

    /**
     * Extract standardized checkout data from Stripe Express Checkout event
     * Converts Stripe's event structure to WooCommerce-compatible format
     * @param {Object} event - Confirm event with billingDetails and shippingAddress
     * @param {Object} paymentMethod - Stripe payment method object
     * @returns {Object} Standardized checkout data with billing, shipping, and payment_method
     */
    extractStandardizedCheckoutData(event, paymentMethod) {
        const data = {
            paymentMethodId: paymentMethod.id
        };

        // Extract billing data
        if (event.billingDetails) {
            const billing = {};
            const {name, email, phone, address} = event.billingDetails;

            // Split name into first and last
            if (name) {
                const nameParts = name.split(' ');
                billing.first_name = nameParts[0] || '';
                billing.last_name = nameParts.slice(1).join(' ') || '';
            }

            if (email) {
                billing.email = email;
            }

            if (phone) {
                billing.phone = phone;
            }

            if (address) {
                billing.address_1 = address.line1 || '';
                billing.address_2 = address.line2 || '';
                billing.city = address.city || '';
                billing.state = address.state || '';
                billing.postcode = address.postal_code || '';
                billing.country = address.country || '';
            }

            data.billing = billing;
        }

        // Extract shipping data
        if (event.shippingAddress) {
            const shipping = {};
            const {name, address} = event.shippingAddress;

            // Split name into first and last
            if (name) {
                const nameParts = name.split(' ');
                shipping.first_name = nameParts[0] || '';
                shipping.last_name = nameParts.slice(1).join(' ') || '';
            }

            if (address) {
                shipping.address_1 = address.line1 || '';
                shipping.address_2 = address.line2 || '';
                shipping.city = address.city || '';
                shipping.state = address.state || '';
                shipping.postcode = address.postal_code || '';
                shipping.country = address.country || '';
            }

            data.shipping = shipping;
        }

        return data;
    }

    /**
     * Handle cancel event when user closes payment sheet
     * @param {Object} event
     */
    onCancel(event) {
        this._isOpen = false;
        this.controller.onCancel();
    }

    /**
     * Handle shipping address change
     * @param {String} type - The event type
     * @param {Object} event - Contains resolve, reject, and address
     */
    async onShippingDataChange(type, event) {
        const {resolve, reject, address = null, shippingRate = null} = event;

        try {
            const shippingData = {};

            if (address) {
                shippingData.address = this.transformIntermediateAddress(address);
            } else {
                shippingData.shipping_method = shippingRate.id;
            }

            await this.controller.onShippingChange(shippingData);

            const shippingOptions = this.controller.getShippingOptions();

            // No shipping options available for the new address; reject so Stripe
            // shows its own "can't ship to this address" state instead of resolving
            // with an empty shippingRates array.
            if (type === 'shippingaddresschange' && shippingOptions.length === 0) {
                console.log('No shipping options available for address, rejecting shippingaddresschange.', address);
                return reject();
            }

            // Update the elements with the new total amount
            if (this.controller.getTotalCents() > 0) {
                this.elements.update({
                    amount: this.controller.getTotalCents()
                });
            }

            // Prepare resolve data
            const resolveData = {
                shippingRates: this.transformShippingRates(
                    shippingOptions,
                    this.controller.getSelectedShippingMethod()
                )
            };

            const lineItems = this.controller.getLineItems();

            // Only include line items if they match the total (prevents Stripe errors)
            if (lineItems && lineItems.length > 0) {
                const calculatedTotal = lineItems.reduce((sum, item) => sum + item.amountCents, 0);
                if (calculatedTotal === this.controller.getTotalCents()) {
                    resolveData.lineItems = this.transformLineItems(lineItems);
                }
            }

            resolve(resolveData);
        } catch (error) {
            reject();
        }
    }

    /**
     * Transform an intermediate address for WooCommerce
     * @param {Object} address - Stripe address object
     * @returns {Object}
     */
    transformIntermediateAddress(address) {
        return {
            city: address.city || '',
            state: address.state || '',
            postcode: address.postal_code || '',
            country: address.country || ''
        }
    }

    /**
     * Transform WooCommerce shipping options to Stripe format
     *
     * Converts generic shipping options to Stripe's Express Checkout Element format.
     * If a selected option is provided, it's moved to the front of the array.
     *
     * @param {Array} shippingOptions - WooCommerce shipping options
     * @param {String} selectedOption - ID of the selected shipping method
     * @returns {Array} Stripe-formatted shipping rates
     */
    transformShippingRates(shippingOptions = [], selectedOption = null) {
        // Map WooCommerce format to Stripe Express Checkout format
        let options = shippingOptions.map(option => ({
            id: option.id,
            displayName: option.label,
            amount: option.amountCents
        }));

        // Move selected option to the front if provided
        if (selectedOption) {
            // Find the index of the selected option
            const selectedIndex = options.findIndex(opt => opt.id === selectedOption);

            // Only move if found and not already first (index > 0)
            if (selectedIndex > 0) {
                // Remove the selected option from its current position
                const [selected] = options.splice(selectedIndex, 1);
                // Add it to the front of the array
                options.unshift(selected);
            }
        }

        return options;
    }

    /**
     * Transform WooCommerce line items to Stripe format
     * @param {Array} lineItems
     * @returns {Array}
     */
    transformLineItems(lineItems = []) {
        return lineItems.map(item => ({
            name: item.label,
            amount: item.amountCents
        }));
    }

    /**
     * Build element options for Stripe
     * @returns {Object}
     */
    buildPaymentElementOptions(isUpdate = false) {
        let options = {
            emailRequired: this.isEmailRequired(),
            phoneNumberRequired: this.isPhoneRequired(),
            billingAddressRequired: this.isBillingAddressRequired(),
            shippingAddressRequired: this.controller.needsShipping(),
            buttonHeight: Math.max(40, Math.min(55, parseInt(this.settings.button?.height || 44))),
            ...(this.supports('button_theme') && {
                buttonTheme: {
                    [this.type]: this.settings.button?.theme || 'black'
                }
            }),
            ...(this.supports('button_type') && {
                buttonType: {
                    [this.type]: this.getButtonType()
                }
            }),
            paymentMethods: {
                applePay: this.type === 'applePay' ? this.settings.display_rule : 'never',
                googlePay: this.type === 'googlePay' ? this.settings.display_rule : 'never',
                amazonPay: 'never',
                paypal: 'never',
                link: this.type === 'link' ? 'auto' : 'never',
            },
            layout: {
                maxColumns: 1,
                maxRows: 1
            },
        };

        return options;
    }

    /**
     * Destroy the payment element
     */
    async destroyPaymentElement() {
        if (this.paymentElement) {
            if (this._isEligible !== null) {
                // We await the eligibility test because if that is interrupted
                // by calling this.paymentElement.destroy(), it will never resolve
                // and can prevent the button from rendering.
                await this.isEligible();
            }
            this.paymentElement.destroy();
            this.paymentElement = null;
            this.createElements();
            this.createPaymentElement();
        }
    }

    /**
     * Disable the payment element
     */
    disablePaymentElement() {
        if (this.paymentElement) {
            this._isDisabled = true;
        }
    }

    /**
     * Enable the payment element
     */
    enablePaymentElement() {
        if (this.paymentElement) {
            this._isDisabled = false;
        }
    }

    closePaymentSheet() {
        this.paymentElement?.unmount();
    }

    getElementOptions(isUpdate = false) {
        const options = super.getElementOptions(isUpdate);
        // express checkout element does not use the "paymentMethodTypes" property.
        delete options.paymentMethodTypes;

        if (this.settings?.button?.radius) {
            options.appearance = {
                ...options.appearance,
                variables: {
                    ...options.appearance?.variables,
                    borderRadius: this.settings.button.radius
                }
            };
        }
        return options;
    }

    isEmailRequired() {
        return isFieldRequired('billing_email')
            && !this.context.isCheckout()
            && !this.context.isOrderPay()
            && !this.context.isAddPaymentMethod()
    }

    isPhoneRequired() {
        return (isFieldRequired('billing_phone') ||
                this.controller.needsShipping() && isFieldRequired('shipping_phone'))
            && !this.context.isCheckout()
            && !this.context.isOrderPay()
            && !this.context.isAddPaymentMethod()
    }

    isBillingAddressRequired() {
        return !this.context.isCheckout()
            && !this.context.isOrderPay()
            && !this.context.isAddPaymentMethod();
    }

    isPaymentSheetOpen() {
        return this._isOpen;
    }

    getButtonType() {
        if (this.settings?.buttonTypes) {
            const context = this.context.getPage();
            if (this.settings.buttonTypes[context]) {
                return this.settings.buttonTypes[context];
            }
        }
        return this.settings.button?.type || 'buy';
    }

}

export default AbstractExpressGateway;